import {
	customFontSchema,
	WHATSAPP_LABELS,
	type WhatsappLabel,
} from "@repo/agent-config";
import { CHAT_LABELS, type ChatLabel } from "@repo/embed";
import type { AgentSettings, ModelEntry } from "./types";

export interface SettingChange {
	label: string;
	from: string;
	to: string;
}

export const HEX = /^#[0-9a-fA-F]{6}$/;

/** What the schema accepts for the privacy notice: a full https:// link. */
export function isHttpsUrl(value: string): boolean {
	return URL.canParse(value) && new URL(value).protocol === "https:";
}

const clip = (value: string, max = 80) =>
	value.length > max ? `${value.slice(0, max - 1)}…` : value;

/** Human-readable differences between two configurations, for publish review. */
export function settingChanges(
	from: AgentSettings,
	to: AgentSettings,
	models: readonly ModelEntry[],
): SettingChange[] {
	const model = (id: string) => models.find((m) => m.id === id)?.label ?? id;
	const list = (values: readonly string[]) =>
		values.length ? values.join(", ") : "none";
	// Content, not only the count, so an edited phrase shows as a change.
	const phrases = (book: AgentSettings["languages"]["phrasebook"]) => {
		const replies = book.flatMap((e) => e.replies.filter((r) => r.trim()));
		return replies.length
			? `${replies.length} (${clip(replies.join(" · "))})`
			: "none";
	};
	const priceCap = (cap: AgentSettings["models"]["maxPricePerMillion"]) =>
		cap ? `$${cap.input} in / $${cap.output} out per 1M tokens` : "none";
	const text = (value: string | undefined) =>
		value?.trim() ? clip(value.trim()) : "empty";

	// Hosting included, so re-pinning a routed model's hosts shows as a change.
	const routes = (m: AgentSettings["models"]) =>
		m.routes.length
			? m.routes
					.map(
						(r) =>
							`${r.languages.join(" + ")}: ${model(r.primary)}${r.providers.length ? ` on ${r.providers.join(", ")}` : ""}, reasoning ${r.reasoningEffort}${r.maxPricePerMillion ? `, ${priceCap(r.maxPricePerMillion)}` : ""}`,
					)
					.join(" · ")
			: "none";

	const fields: Array<[string, string, string]> = [
		["Primary model", model(from.models.primary), model(to.models.primary)],
		[
			"Fallback models",
			list(from.models.fallbacks.map(model)),
			list(to.models.fallbacks.map(model)),
		],
		[
			"Provider order",
			list(from.models.providers),
			list(to.models.providers),
		],
		[
			"Host selection",
			from.models.providerSort ?? "automatic",
			to.models.providerSort ?? "automatic",
		],
		[
			"Host price ceiling",
			priceCap(from.models.maxPricePerMillion),
			priceCap(to.models.maxPricePerMillion),
		],
		["Reasoning", from.models.reasoningEffort, to.models.reasoningEffort],
		["Model by language", routes(from.models), routes(to.models)],
		[
			"Temperature",
			String(from.models.temperature),
			String(to.models.temperature),
		],
		[
			"Max reply tokens",
			String(from.models.maxOutputTokens),
			String(to.models.maxOutputTokens),
		],
		[
			"Tool rounds per message",
			String(from.models.maxSteps),
			String(to.models.maxSteps),
		],
		[
			"Assistant name",
			from.persona.assistantName,
			to.persona.assistantName,
		],
		[
			"Retailer description",
			text(from.persona.retailerDescription),
			text(to.persona.retailerDescription),
		],
		["Tone", text(from.persona.tone), text(to.persona.tone)],
		[
			"Approved replies",
			phrases(from.languages.phrasebook),
			phrases(to.languages.phrasebook),
		],
		[
			"Languages",
			list(from.languages.supported),
			list(to.languages.supported),
		],
		[
			"Language rules",
			text(from.languages.rules),
			text(to.languages.rules),
		],
		["Tools", list(from.tools.enabled), list(to.tools.enabled)],
		["Starter prompts", list(from.starterPrompts), list(to.starterPrompts)],
		[
			"Retailer rules",
			text(from.extraInstructions),
			text(to.extraInstructions),
		],
		[
			"Default store",
			from.defaultStoreExternalId ?? "first store",
			to.defaultStoreExternalId ?? "first store",
		],
		[
			"Brand colour",
			from.appearance.primaryColor,
			to.appearance.primaryColor,
		],
		["Deep colour", from.appearance.deepColor, to.appearance.deepColor],
		[
			"Accent colour",
			from.appearance.accentColor,
			to.appearance.accentColor,
		],
		["Deal colour", from.appearance.dealColor, to.appearance.dealColor],
		[
			"Header line",
			text(from.appearance.tagline),
			text(to.appearance.tagline),
		],
		["Typeface", typeface(from.appearance), typeface(to.appearance)],
		["Colour scheme", from.appearance.scheme, to.appearance.scheme],
		["Corners", from.appearance.corners, to.appearance.corners],
		["Header style", from.appearance.header, to.appearance.header],
		["Shopper's messages", from.appearance.bubble, to.appearance.bubble],
		[
			"App overrides",
			onOff(from.appearance.allowAppOverrides),
			onOff(to.appearance.allowAppOverrides),
		],
		[
			"Welcome title",
			text(from.chat.welcomeTitle),
			text(to.chat.welcomeTitle),
		],
		[
			"Welcome line",
			text(from.chat.welcomeSubtitle),
			text(to.chat.welcomeSubtitle),
		],
		["Starter prompt style", from.chat.starterStyle, to.chat.starterStyle],
		[
			"Message box placeholder",
			text(from.chat.placeholder),
			text(to.chat.placeholder),
		],
		[
			"Logo in the chat",
			onOff(from.chat.showLogo),
			onOff(to.chat.showLogo),
		],
		[
			"Suggested replies",
			onOff(from.chat.followUps),
			onOff(to.chat.followUps),
		],
		["Button shape", from.appearance.buttons, to.appearance.buttons],
		...PART_COLORS.map(
			({ key, label }) =>
				[
					label,
					from.appearance[key] ?? "automatic",
					to.appearance[key] ?? "automatic",
				] as [string, string, string],
		),
		...SHOWN.map(
			({ key, label }) =>
				[label, onOff(from.chat[key]), onOff(to.chat[key])] as [
					string,
					string,
					string,
				],
		),
		[
			"Powered by Hephon",
			onOff(from.chat.poweredBy),
			onOff(to.chat.poweredBy),
		],
		...CHAT_LABELS.map(
			(label) =>
				[
					`Wording: ${LABEL_TEXT[label].name}`,
					text(from.chat.labels[label]),
					text(to.chat.labels[label]),
				] as [string, string, string],
		),
		["Voice", voiceFeatures(from.voice), voiceFeatures(to.voice)],
		[
			"Voice beta notice",
			onOff(from.voice.betaNotice),
			onOff(to.voice.betaNotice),
		],
		["Arabic voice", arabicVoice(from.voice), arabicVoice(to.voice)],
		[
			"Call greeting",
			text(from.voice.calls.greeting),
			text(to.voice.calls.greeting),
		],
		[
			"Say it is an AI assistant on calls",
			onOff(from.voice.calls.aiNotice),
			onOff(to.voice.calls.aiNotice),
		],
		[
			"Recording notice",
			text(from.voice.calls.recordingNotice),
			text(to.voice.calls.recordingNotice),
		],
		[
			"Silence before a call ends",
			`${from.voice.calls.silenceSeconds} s`,
			`${to.voice.calls.silenceSeconds} s`,
		],
		[
			"WhatsApp voice notes",
			VOICE_REPLY_TEXT[from.whatsapp.voiceReplies],
			VOICE_REPLY_TEXT[to.whatsapp.voiceReplies],
		],
		[
			"WhatsApp location question",
			ASK_LOCATION_TEXT[from.whatsapp.askLocation],
			ASK_LOCATION_TEXT[to.whatsapp.askLocation],
		],
		[
			"WhatsApp products",
			PRODUCT_MESSAGE_TEXT[from.whatsapp.productMessages],
			PRODUCT_MESSAGE_TEXT[to.whatsapp.productMessages],
		],
		[
			"Read photos on WhatsApp",
			onOff(from.whatsapp.readPhotos),
			onOff(to.whatsapp.readPhotos),
		],
		[
			"WhatsApp privacy line",
			text(from.whatsapp.privacyLine),
			text(to.whatsapp.privacyLine),
		],
		...WHATSAPP_LABELS.map(
			(label) =>
				[
					`WhatsApp wording: ${WHATSAPP_LABEL_NAME[label]}`,
					labelText(from.whatsapp.labels[label]),
					labelText(to.whatsapp.labels[label]),
				] as [string, string, string],
		),
		["Order help", onOff(from.support.enabled), onOff(to.support.enabled)],
		[
			"Privacy notice",
			from.support.privacyUrl ?? "Hephon's page",
			to.support.privacyUrl ?? "Hephon's page",
		],
		[
			"Handoff message",
			text(from.handoff.message),
			text(to.handoff.message),
		],
		[
			"Team hours",
			text(from.handoff.hoursText),
			text(to.handoff.hoursText),
		],
		[
			"Substitutions",
			substitutionText(from.substitutions),
			substitutionText(to.substitutions),
		],
		["Shopper memory", memoryText(from.memory), memoryText(to.memory)],
		[
			"Never sells",
			restrictionsText(from.restrictions),
			restrictionsText(to.restrictions),
		],
	];

	return fields
		.filter(([, a, b]) => a !== b)
		.map(([label, a, b]) => ({ label, from: a, to: b }));
}

const onOff = (on: boolean) => (on ? "on" : "off");

/** A WhatsApp label in every language the retailer wrote it in. */
const labelText = (texts: Record<string, string> | undefined) => {
	const entries = Object.entries(texts ?? {});
	return entries.length
		? clip(entries.map(([lang, value]) => `${lang}: ${value}`).join(" · "))
		: "built-in";
};

export const VOICE_REPLY_TEXT = {
	mirror: "a voice note answers a voice note",
	text: "always in writing",
} as const;

export const ASK_LOCATION_TEXT = {
	first: "before any product is shown",
	when_needed: "with the first basket",
	at_checkout: "only at checkout",
	never: "never",
} as const;

export const PRODUCT_MESSAGE_TEXT = {
	auto: "catalog messages when every product is in the Meta catalog",
	media: "always picture cards",
	list: "always a plain list",
} as const;

/** What each WhatsApp label is, in the admin's words. */
export const WHATSAPP_LABEL_NAME: Record<WhatsappLabel, string> = {
	checkout: "Checkout button",
	editBasket: "Edit basket button",
	keepShopping: "Keep shopping button",
	add: "Add button",
	similar: "Similar button",
	basket: "Basket button",
	addAll: "Add all button",
	chooseItems: "Choose items button",
	steps: "Recipe steps button",
	basketTitle: "Basket summary title",
	listTitle: "Shopping list title",
	pricesFrom: "Prices from a branch",
	locationAsk: "Where to deliver question",
	deliverAgain: "Deliver there again button",
	somewhereElse: "Somewhere else button",
	newLocation: "New location row",
	pickUpThere: "Pick up there button",
	anotherLocation: "Another location button",
	talkToPerson: "Talk to a person button",
};

/** A part of the chat that can have a colour of its own; absent follows the brand's. */
export const PART_COLORS = [
	{
		key: "ctaColor",
		label: "Basket and checkout buttons",
		info: "“View basket” and the checkout button. Automatic: the deep colour and the brand colour.",
	},
	{
		key: "headerColor",
		label: "Header",
		info: "Fills the header bar. Automatic: the page's colour, or the brand colour when the header is set to it.",
	},
	{
		key: "bubbleColor",
		label: "Shopper's messages",
		info: "Automatic: the brand colour, or its tint.",
	},
	{
		key: "surfaceColor",
		label: "Page background",
		info: "The page behind everything. A dark one turns the whole chat dark, whatever the colour scheme says.",
	},
	{
		key: "cardColor",
		label: "Cards and sheets",
		info: "Product cards, the basket, the product sheet and menus. Automatic: white, or a shade lighter than a dark page.",
	},
	{
		key: "textColor",
		label: "Text",
		info: "Replies, titles and prices. Automatic: near black, or near white on a dark page. Check it reads on the page and on cards.",
	},
	{
		key: "mutedColor",
		label: "Secondary text",
		info: "Sizes, item counts and other small print. Automatic: a grey that reads on the page.",
	},
	{
		key: "lineColor",
		label: "Lines and borders",
		info: "The outline of cards, chips and the message box. Automatic: a faint tint of the brand colour.",
	},
] as const;

/** What the chat shows, each a switch. */
export const SHOWN = [
	{
		key: "showHeader",
		label: "Header",
		description:
			"The bar with your logo, the assistant's name and the menu. Off when your app has its own bar above the chat. The website widget keeps it: its close button is there.",
	},
	{
		key: "showBack",
		label: "Back button",
		description:
			"An arrow before your logo that closes the chat. For an app that opens the chat as a full page instead of a sheet: your app gets the close event.",
	},
	{
		key: "showBasket",
		label: "Basket",
		description:
			"The basket button, the bar with the total, and the basket itself. Off when your app shows its own cart: it still gets every cart event.",
	},
	{
		key: "showBasketButton",
		label: "Basket button in the header",
		description:
			"The button with the item count. Off keeps the basket reachable from the bar above the message box.",
	},
	{
		key: "showBasketBar",
		label: "Basket summary",
		description:
			"The bar above the message box with the item count, the total and “View basket”. Off keeps the basket reachable from the header.",
	},
	{
		key: "showCheckout",
		label: "Checkout button",
		description:
			"In the basket. Off when shoppers check out from your app's own cart.",
	},
	{
		key: "showNewChat",
		label: "Start a new chat",
		description: "In the header's menu, once a conversation has begun.",
	},
	{
		key: "showAssistantName",
		label: "Assistant's name on replies",
		description: "The name above each reply, beside your logo.",
	},
	{
		key: "showWelcome",
		label: "Welcome screen",
		description:
			"The title, the line under it and the suggestions. Off when your app draws its own first screen above the chat and sends what the shopper picks.",
	},
	{
		key: "showStarters",
		label: "Starter prompts",
		description:
			"The suggested first messages on the welcome screen. Off leaves the welcome title and the message box.",
	},
	{
		key: "showSteps",
		label: "What the assistant is doing",
		description:
			"A line such as “Searching the store” above its results. A step that fails is always shown.",
	},
	{
		key: "productSheet",
		label: "Product details",
		description:
			"Tapping a product opens its details in the chat. Off when your app opens its own product page (it is told which product either way).",
	},
	{
		key: "addFromCards",
		label: "Add from product cards",
		description:
			"The + on each product, so a shopper adds without asking the assistant.",
	},
] as const;

/** Each replaceable label: what the admin sees it called, and the built-in wording. */
export const LABEL_TEXT: Record<ChatLabel, { name: string; builtIn: string }> =
	{
		viewBasket: { name: "View basket button", builtIn: "View basket" },
		basketTitle: { name: "Basket title", builtIn: "Your basket" },
		checkout: {
			name: "Checkout button",
			builtIn: "Check out with {retailer}",
		},
		addToBasket: { name: "Add button", builtIn: "Add to basket" },
		orderHelp: { name: "Order help", builtIn: "Help with an order" },
		newChat: { name: "New chat", builtIn: "Start a new chat" },
		total: { name: "Basket total", builtIn: "Total" },
		emptyBasket: { name: "Empty basket", builtIn: "Nothing here yet" },
		thinking: { name: "Assistant is thinking", builtIn: "Thinking" },
		typing: { name: "Assistant is typing", builtIn: "Typing" },
		tryAgain: { name: "Try again button", builtIn: "Try again" },
	};

function substitutionText(substitutions: AgentSettings["substitutions"]) {
	const fallback =
		substitutions.default &&
		{ SIMILAR: "similar item", NONE: "leave it out" }[
			substitutions.default
		];
	if (substitutions.allowed) {
		return fallback
			? `shopper chooses, else ${fallback}`
			: "shopper chooses";
	}
	return fallback ? `always ${fallback}` : "off";
}

/** The restrictions the retailer can choose, in the editor's words. */
export const RESTRICTION_OPTIONS = [
	{
		kind: "alcohol",
		label: "Alcohol",
		description: "Wine, beer, spirits, and food made with alcohol",
	},
	{
		kind: "alcohol_free",
		label: "Alcohol-free beer, wine and spirits",
		description:
			"0.0% versions of alcoholic drinks. Malt soft drinks stay.",
	},
	{
		kind: "pork",
		label: "Pork",
		description:
			"Ham, bacon, salami, lard, pork gelatin and anything made with them",
	},
	{
		kind: "tobacco",
		label: "Tobacco",
		description: "Cigarettes, shisha tobacco, vapes and nicotine pouches",
	},
] as const satisfies ReadonlyArray<{
	kind: AgentSettings["restrictions"]["exclude"][number];
	label: string;
	description: string;
}>;

function restrictionsText(restrictions: AgentSettings["restrictions"]) {
	const excluded = new Set(restrictions.exclude);
	const kinds = RESTRICTION_OPTIONS.filter((o) => excluded.has(o.kind)).map(
		(o) => o.label.toLowerCase(),
	);
	if (!kinds.length) {
		return "nothing restricted";
	}
	return `${kinds.join(", ")}${restrictions.hideDoubtful ? ", and doubtful products" : ""}`;
}

function memoryText(memory: AgentSettings["memory"]) {
	const remembered = new Set(memory.remember);
	const kept = MEMORY_KINDS.filter((k) => remembered.has(k.kind)).map((k) =>
		k.label.toLowerCase(),
	);
	return kept.length ? kept.join(", ") : "off";
}

/** What is wrong with the name of an uploaded font, in the settings schema's words. */
export function fontFamilyProblem(family: string): string | null {
	return (
		customFontSchema.shape.family.safeParse(family).error?.issues[0]
			?.message ?? null
	);
}

/** An uploaded font replaces the preset; a new upload under the same name still counts. */
function typeface(appearance: AgentSettings["appearance"]) {
	const custom = appearance.customFont;
	return custom
		? `${custom.family} (uploaded file ${custom.path.split("/").at(-1)?.slice(0, 6)})`
		: { geist: "Geist", manrope: "Manrope", system: "Device font" }[
				appearance.font
			];
}

function arabicVoice({ arabic }: AgentSettings["voice"]) {
	return arabic.engine === "hamsa"
		? "Lebanese (Hamsa)"
		: `ElevenLabs ${arabic.voiceId || "(no voice)"}`;
}

function voiceFeatures(voice: AgentSettings["voice"]) {
	const on = [
		voice.dictation && "dictation",
		voice.voiceNotes && "voice notes",
		voice.live && "live voice",
	].filter(Boolean);
	return on.length ? on.join(", ") : "off";
}

export const sameSettings = (a: AgentSettings, b: AgentSettings) =>
	JSON.stringify(a) === JSON.stringify(b);

export type MemoryKind = AgentSettings["memory"]["remember"][number];

/** What each kind keeps. A Record, so a kind added to the schema is a type error until it is described here. */
const MEMORY_KIND_TEXT: Record<
	MemoryKind,
	{ label: string; description: string }
> = {
	dietary: {
		label: "Diets and allergies",
		description: "Vegetarian, gluten free, a nut allergy.",
	},
	preferences: {
		label: "Preferences",
		description: "Favourite brands, pack sizes, how they like things.",
	},
	usual: {
		label: "Usual items",
		description:
			"Products they buy regularly, so the assistant can offer them again.",
	},
};

/** The kinds in the order the editor lists them. */
export const MEMORY_KINDS = (
	Object.entries(MEMORY_KIND_TEXT) as Array<
		[MemoryKind, { label: string; description: string }]
	>
).map(([kind, text]) => ({ kind, ...text }));

/** The Configure screen's tabs, in order. */
export const EDITOR_TABS = [
	{
		id: "assistant",
		label: "Assistant",
		hint: "Who it is, the languages it speaks and your rules",
	},
	{
		id: "look",
		label: "Look and feel",
		hint: "Colours, the header, the welcome screen and the conversation",
	},
	{
		id: "behaviour",
		label: "Behaviour",
		hint: "What the chat shows, its wording, what your app may change and WhatsApp",
	},
	{
		id: "voice",
		label: "Voice",
		hint: "Dictation, voice notes, live voice and phone calls",
	},
	{
		id: "shopping",
		label: "Shopping",
		hint: "What the store never sells, tools, stores, substitutions and memory",
	},
	{
		id: "support",
		label: "Support",
		hint: "Order help, handing over to your team and privacy",
	},
	{
		id: "model",
		label: "Model",
		hint: "The model behind it and what it costs",
	},
] as const;

export type TabId = (typeof EDITOR_TABS)[number]["id"];

/** Type-checks a section list while keeping its ids literal. */
const sections = <const T extends readonly EditorSectionDef[]>(list: T) => list;

interface EditorSectionDef {
	id: string;
	tab: TabId;
	label: string;
	/** What the section edits: compared to mark it edited. */
	pick: (s: AgentSettings) => unknown;
}

/** The editor's sections, in tab and page order. `pick` selects what each one edits. */
export const EDITOR_SECTIONS = sections([
	{
		id: "persona",
		tab: "assistant",
		label: "Persona",
		pick: (s) => s.persona,
	},
	{
		id: "languages",
		tab: "assistant",
		label: "Languages",
		pick: (s) => s.languages,
	},
	{
		id: "rules",
		tab: "assistant",
		label: "Retailer rules",
		pick: (s) => s.extraInstructions,
	},
	{
		id: "brand",
		tab: "look",
		label: "Brand",
		pick: ({ appearance: a }) => [
			a.primaryColor,
			a.deepColor,
			a.accentColor,
			a.dealColor,
			a.scheme,
			a.corners,
			a.buttons,
			a.font,
			a.customFont,
		],
	},
	{
		id: "part-colors",
		tab: "look",
		label: "Colours of single parts",
		pick: ({ appearance: a }) => PART_COLORS.map(({ key }) => a[key]),
	},
	{
		id: "header",
		tab: "look",
		label: "Header",
		pick: (s) => [
			s.appearance.header,
			s.appearance.tagline,
			s.chat.showLogo,
		],
	},
	{
		id: "welcome",
		tab: "look",
		label: "Welcome screen",
		pick: (s) => [
			s.chat.welcomeTitle,
			s.chat.welcomeSubtitle,
			s.chat.starterStyle,
			s.starterPrompts,
		],
	},
	{
		id: "conversation",
		tab: "look",
		label: "Conversation",
		pick: (s) => [
			s.appearance.bubble,
			s.chat.placeholder,
			s.chat.followUps,
		],
	},
	{
		id: "shown",
		tab: "behaviour",
		label: "What the chat shows",
		pick: (s) => [...SHOWN.map(({ key }) => s.chat[key]), s.chat.poweredBy],
	},
	{
		id: "wording",
		tab: "behaviour",
		label: "Wording",
		pick: (s) => s.chat.labels,
	},
	{
		id: "overrides",
		tab: "behaviour",
		label: "What your app can change",
		pick: (s) => s.appearance.allowAppOverrides,
	},
	{
		id: "whatsapp",
		tab: "behaviour",
		label: "On WhatsApp",
		pick: ({ whatsapp: w }) => [
			w.voiceReplies,
			w.askLocation,
			w.productMessages,
			w.readPhotos,
			w.labels,
		],
	},
	{
		id: "voice-input",
		tab: "voice",
		label: "Voice messages",
		pick: (s) => [s.voice.dictation, s.voice.voiceNotes],
	},
	{
		id: "voice-live",
		tab: "voice",
		label: "Live voice",
		pick: (s) => [s.voice.live, s.voice.betaNotice, s.voice.arabic],
	},
	{
		id: "phone-calls",
		tab: "voice",
		label: "Phone calls",
		pick: (s) => s.voice.calls,
	},
	{
		id: "restrictions",
		tab: "shopping",
		label: "Never sells",
		pick: (s) => s.restrictions,
	},
	{ id: "tools", tab: "shopping", label: "Tools", pick: (s) => s.tools },
	{
		id: "store",
		tab: "shopping",
		label: "Default store",
		pick: (s) => s.defaultStoreExternalId,
	},
	{
		id: "substitutions",
		tab: "shopping",
		label: "Substitutions",
		pick: (s) => s.substitutions,
	},
	{
		id: "memory",
		tab: "shopping",
		label: "Shopper memory",
		pick: (s) => s.memory,
	},
	{
		id: "order-help",
		tab: "support",
		label: "Order help",
		pick: (s) => s.support.enabled,
	},
	{ id: "handoff", tab: "support", label: "Handoff", pick: (s) => s.handoff },
	{
		id: "privacy",
		tab: "support",
		label: "Privacy notice",
		pick: (s) => s.support.privacyUrl,
	},
	{
		id: "whatsapp-privacy",
		tab: "support",
		label: "Privacy line on WhatsApp",
		pick: (s) => s.whatsapp.privacyLine,
	},
	{
		id: "models",
		tab: "model",
		label: "Model and cost",
		pick: (s) => s.models,
	},
]);

export type SectionId = (typeof EDITOR_SECTIONS)[number]["id"];

/** The tab a section sits on. */
export function tabOf(section: SectionId): TabId {
	return (
		EDITOR_SECTIONS.find((s) => s.id === section)?.tab ?? EDITOR_TABS[0].id
	);
}

/** Sections whose values differ between two configurations. */
export function editedSections(a: AgentSettings, b: AgentSettings) {
	return new Set<SectionId>(
		EDITOR_SECTIONS.filter(
			(section) =>
				JSON.stringify(section.pick(a)) !==
				JSON.stringify(section.pick(b)),
		).map((section) => section.id),
	);
}

export interface SettingsProblem {
	section: SectionId;
	message: string;
	/** Saving a draft is fine; only publishing is blocked. */
	publishOnly?: boolean;
}

/** Everything that must be fixed before the configuration can be saved or published. */
export function settingsProblems(
	settings: AgentSettings,
	models: readonly ModelEntry[],
): SettingsProblem[] {
	const problems: SettingsProblem[] = [];
	const { models: m, persona, languages } = settings;
	const whole = (value: number, min: number, max: number) =>
		Number.isInteger(value) && value >= min && value <= max;

	const byId = new Map(models.map((model) => [model.id, model]));
	const supported = new Set(languages.supported);
	const primary = byId.get(m.primary);
	if (primary && !primary.available) {
		problems.push({
			section: "models",
			message: "The primary model is no longer on OpenRouter.",
			publishOnly: true,
		});
	}
	for (const route of m.routes) {
		const gone = route.languages.filter((l) => !supported.has(l));
		if (gone.length) {
			problems.push({
				section: "models",
				message: `A model is set for ${gone.join(", ")}, which is no longer a supported language.`,
			});
		}
		const routed = byId.get(route.primary);
		if (routed && !routed.available) {
			problems.push({
				section: "models",
				message: `${routed.label} (${route.languages.join(", ")}) is no longer on OpenRouter.`,
				publishOnly: true,
			});
		}
	}
	if (!whole(m.maxOutputTokens, 200, 8000)) {
		problems.push({
			section: "models",
			message:
				"Max reply tokens must be a whole number from 200 to 8,000.",
		});
	}
	if (!whole(m.maxSteps, 1, 12)) {
		problems.push({
			section: "models",
			message: "Tool rounds must be a whole number from 1 to 12.",
		});
	}
	if (!persona.assistantName.trim()) {
		problems.push({
			section: "persona",
			message: "The assistant needs a name.",
		});
	}
	if (persona.tone.length > 200) {
		problems.push({
			section: "persona",
			message: "Tone is over 200 characters.",
		});
	}
	if (persona.retailerDescription.length > 600) {
		problems.push({
			section: "persona",
			message: "About the retailer is over 600 characters.",
		});
	}
	if (languages.supported.length === 0) {
		problems.push({
			section: "languages",
			message: "Add at least one language.",
		});
	}
	if (languages.rules.length > 4000) {
		problems.push({
			section: "languages",
			message: "Dialect and wording rules are over 4,000 characters.",
		});
	}
	if (settings.starterPrompts.some((prompt) => !prompt.trim())) {
		problems.push({
			section: "welcome",
			message: "Fill in or remove the empty starter prompt.",
		});
	}
	const colors = settings.appearance;
	if (
		[
			colors.primaryColor,
			colors.deepColor,
			colors.accentColor,
			colors.dealColor,
		].some((value) => !HEX.test(value))
	) {
		problems.push({
			section: "brand",
			message: "Brand colours must be hex, like #1E1EF2.",
		});
	}
	if (PART_COLORS.some(({ key }) => colors[key] && !HEX.test(colors[key]))) {
		problems.push({
			section: "part-colors",
			message: "A part's colour must be hex, like #1E1EF2.",
		});
	}
	if (
		CHAT_LABELS.some(
			(label) => (settings.chat.labels[label] ?? "").length > 40,
		)
	) {
		problems.push({
			section: "wording",
			message: "A label is over 40 characters.",
		});
	}
	const fontProblem = colors.customFont
		? fontFamilyProblem(colors.customFont.family)
		: null;
	if (fontProblem) {
		problems.push({
			section: "brand",
			message: `Font name: ${fontProblem}.`,
		});
	}
	const { privacyUrl } = settings.support;
	if (privacyUrl !== undefined && !isHttpsUrl(privacyUrl)) {
		problems.push({
			section: "privacy",
			message: "The privacy notice must be a full https:// link.",
		});
	}
	const { arabic } = settings.voice;
	if (arabic.engine === "elevenlabs" && !arabic.voiceId) {
		problems.push({
			section: "voice-live",
			message: "Pick an ElevenLabs voice for Arabic, or keep Hamsa.",
		});
	}
	const { chat } = settings;
	if (chat.welcomeTitle.length > 80) {
		problems.push({
			section: "welcome",
			message: "The welcome title is over 80 characters.",
		});
	}
	if (chat.welcomeSubtitle.length > 140) {
		problems.push({
			section: "welcome",
			message: "The line under the welcome title is over 140 characters.",
		});
	}
	if (chat.placeholder.length > 60) {
		problems.push({
			section: "conversation",
			message: "The message box placeholder is over 60 characters.",
		});
	}
	const { handoff } = settings;
	if (!handoff.message.trim()) {
		problems.push({
			section: "handoff",
			message: "Write what the shopper is told when handed to your team.",
		});
	}
	if (handoff.message.length > 300) {
		problems.push({
			section: "handoff",
			message: "The handoff message is over 300 characters.",
		});
	}
	if ((handoff.hoursText ?? "").length > 120) {
		problems.push({
			section: "handoff",
			message: "Team hours are over 120 characters.",
		});
	}
	if (settings.extraInstructions.length > 4000) {
		problems.push({
			section: "rules",
			message: "Retailer rules are over 4,000 characters.",
		});
	}
	const { calls } = settings.voice;
	if (!calls.greeting.trim() || calls.greeting.length > 200) {
		problems.push({
			section: "phone-calls",
			message: "The call greeting must be 1 to 200 characters.",
		});
	}
	if (!calls.recordingNotice.trim() || calls.recordingNotice.length > 200) {
		problems.push({
			section: "phone-calls",
			message: "The recording notice must be 1 to 200 characters.",
		});
	}
	if (!whole(calls.silenceSeconds, 5, 120)) {
		problems.push({
			section: "phone-calls",
			message: "Silence before a call ends must be 5 to 120 seconds.",
		});
	}
	const { whatsapp } = settings;
	if (!whatsapp.privacyLine.trim() || whatsapp.privacyLine.length > 300) {
		problems.push({
			section: "whatsapp-privacy",
			message: "The WhatsApp privacy line must be 1 to 300 characters.",
		});
	}
	if (
		WHATSAPP_LABELS.some((label) =>
			Object.values(whatsapp.labels[label] ?? {}).some(
				(value) => !value.trim() || value.length > 60,
			),
		)
	) {
		problems.push({
			section: "whatsapp",
			message: "A WhatsApp label is empty or over 60 characters.",
		});
	}
	return problems;
}
